import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  LuArrowLeft,
  LuBot,
  LuChevronDown,
  LuChevronRight,
  LuCircleStop,
  LuGitPullRequest,
  LuLoaderPinwheel,
  LuRefreshCw,
} from 'react-icons/lu';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  ADR_CHECK_LABEL,
  CODE_AGENT_DEFAULT_TIER,
  CODE_AGENT_TIER_LABEL,
  CODE_AGENT_TIER_RESOURCES,
  CodeAgentLogEntry,
  CodeAgentTask,
  CodeAgentTaskStatus,
  isAutomatedAdrCheck,
  PR_REVIEW_RESUMABLE_STATUSES,
  PR_REVIEW_STATUS_BADGE,
  PR_REVIEW_STATUS_LABEL,
} from '../../entities/code-agent-task';
import { StandaloneTask } from '../../entities/standalone-task';
import {
  checkPrReviewNow,
  codeAgentErrorMessage,
  getCodeAgentTask,
  resumeCodeAgentTask,
  stopCodeAgentTask,
} from '../../shared/api/code-agent';
import { getStandaloneTask } from '../../shared/api/standalone';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { formatRelativeTime } from '../../shared/utils/time';
import MarkdownRenderer from '../../widgets/MarkdownRenderer';
import { LinkedTaskPreviewModal } from '../../widgets/standalone/LinkedTaskPreviewModal';

const STATUS_BADGE: Record<CodeAgentTaskStatus, string> = {
  queued: 'badge-ghost',
  running: 'badge-info',
  completed: 'badge-success',
  failed: 'badge-error',
  cancelled: 'badge-warning',
};

const STOPPABLE_STATUSES: CodeAgentTaskStatus[] = ['queued', 'running'];
const TERMINAL_STATUSES: CodeAgentTaskStatus[] = ['completed', 'failed', 'cancelled'];

const formatTokenCount = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n));

interface ProgressPayload {
  userId: string;
  taskId: string;
  status: CodeAgentTaskStatus;
  logEntry?: CodeAgentLogEntry;
  resultSummary?: string;
  error?: string;
  queuePosition?: number;
}

// Live execution-log stream for one task — codeAgentTaskProgress fires per status change and per
// log line (mflow knowledge base "Autonomous coding agent" doc). Seeds from the REST snapshot's
// persisted `logs[]` (anything that happened before this page opened, or a full page reload), then
// appends live entries as they stream in — same seed-then-append pattern the chat surfaces use for
// history vs. live messages.
export default function CodeAgentTaskPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { on, off } = useSocket(auth.currentUser?.uid || '');

  const [task, setTask] = useState<CodeAgentTask | null>(null);
  const [logs, setLogs] = useState<CodeAgentLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isStopping, setIsStopping] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  const [isResuming, setIsResuming] = useState(false);
  const [isCheckingPr, setIsCheckingPr] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  // Fetched by id (not passed via navigation state) so the link shows the real key/title on any
  // page load, not just right after creation. Best-effort — a deleted linked task just hides it.
  const [linkedTask, setLinkedTask] = useState<StandaloneTask | null>(null);
  const [isLinkedTaskModalOpen, setIsLinkedTaskModalOpen] = useState(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    getCodeAgentTask(taskId)
      .then((t) => {
        if (cancelled) return;
        setTask(t);
        setLogs(t.logs ?? []);
      })
      .catch((err) => !cancelled && setLoadError(codeAgentErrorMessage(err) ?? 'Failed to load this task.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => {
    if (!task?.standaloneTaskId) return;
    let cancelled = false;
    getStandaloneTask(task.standaloneTaskId)
      .then((t) => !cancelled && setLinkedTask(t))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [task?.standaloneTaskId]);

  useEffect(() => {
    if (!taskId) return;
    const onProgress = (data: ProgressPayload) => {
      if (data.taskId !== taskId) return;
      if (data.logEntry) setLogs((prev) => [...prev, data.logEntry!]);
      setTask((prev) =>
        prev
          ? {
              ...prev,
              status: data.status,
              resultSummary: data.resultSummary ?? prev.resultSummary,
              error: data.error ?? prev.error,
              queuePosition: data.status === 'queued' ? data.queuePosition : undefined,
            }
          : prev,
      );
      // tokenUsage/costUsd aren't part of the WS payload — only the REST response carries them,
      // and only once the task is actually done. Re-fetch on the terminal transition so a page
      // left open while the task runs picks up the real cost without a manual refresh.
      if (TERMINAL_STATUSES.includes(data.status)) {
        getCodeAgentTask(taskId)
          .then(setTask)
          .catch(() => {});
      }
    };
    on('codeAgentTaskProgress', onProgress);
    return () => off('codeAgentTaskProgress', onProgress as (...args: unknown[]) => void);
  }, [taskId, on, off]);

  useEffect(() => {
    if (!isLogsOpen) return;
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs.length, isLogsOpen]);

  // `stopped: false` just means the task already finished before the request landed — a race, not
  // a failure, so it's not surfaced as an error. Either way the actual status change (or lack of
  // one) arrives via the codeAgentTaskProgress WS handler above, not this response.
  const handleStop = async () => {
    if (!taskId) return;
    setIsStopping(true);
    try {
      await stopCodeAgentTask(taskId);
    } catch (err) {
      toast.error(codeAgentErrorMessage(err) ?? 'Failed to stop the task.');
    } finally {
      setIsStopping(false);
    }
  };

  // MF-50 — always creates a NEW task (never mutates this one); navigating there is the "show it
  // as a follow-up under the original" affordance until a real threaded list view exists.
  const handleResume = async () => {
    if (!taskId) return;
    setIsResuming(true);
    try {
      const resumed = await resumeCodeAgentTask(taskId, additionalContext.trim() || undefined);
      toast.success('Resuming with review feedback…');
      navigate(`/coding-agent/tasks/${resumed._id}`);
    } catch (err) {
      toast.error(codeAgentErrorMessage(err) ?? 'Failed to resume the task.');
    } finally {
      setIsResuming(false);
    }
  };

  // MF-50 §2b — on-demand refresh instead of waiting on the 15-minute scheduled poll. Purely
  // additive; nothing else here depends on this ever being clicked.
  const handleCheckPrNow = async () => {
    if (!taskId) return;
    setIsCheckingPr(true);
    try {
      const updated = await checkPrReviewNow(taskId);
      setTask(updated);
    } catch (err) {
      toast.error(codeAgentErrorMessage(err) ?? 'Failed to check the PR right now.');
    } finally {
      setIsCheckingPr(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <LuLoaderPinwheel className="animate-spin opacity-40" size={28} />
      </div>
    );
  }

  if (loadError || !task) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center p-6">
        <div className="alert alert-error max-w-sm flex-col items-start gap-2">
          <span>{loadError ?? 'Task not found.'}</span>
          <Link to="/coding-agent" className="btn btn-sm btn-ghost">
            Back to coding agent
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/coding-agent" className="btn btn-ghost btn-sm btn-circle shrink-0" title="Back">
            <LuArrowLeft size={16} />
          </Link>
          <span className={`badge badge-sm shrink-0 ${STATUS_BADGE[task.status]}`}>{task.status}</span>
          {task.status === 'queued' && task.queuePosition != null && (
            <span
              className="badge badge-ghost badge-sm shrink-0"
              title="Approximate — can shift as other tasks finish or are cancelled"
            >
              Position {task.queuePosition}
            </span>
          )}
          <p className={`text-sm truncate flex-1 ${isAutomatedAdrCheck(task) ? 'italic opacity-60' : 'font-medium'}`}>
            {isAutomatedAdrCheck(task) ? ADR_CHECK_LABEL : task.prompt}
          </p>
          {STOPPABLE_STATUSES.includes(task.status) && (
            <button
              className="btn btn-ghost btn-sm gap-1.5 text-error shrink-0"
              onClick={handleStop}
              disabled={isStopping}
              title="Stop this task"
            >
              {isStopping ? <LuLoaderPinwheel className="animate-spin" size={14} /> : <LuCircleStop size={14} />}
              Stop
            </button>
          )}
        </div>

        {task.repoUrl && (
          <p className="text-xs opacity-50 pl-1">
            Repo: {task.repoUrl}
            {task.branch && ` @ ${task.branch}`}
          </p>
        )}

        <div className="flex items-center gap-1.5 pl-1">
          <span className="badge badge-outline badge-sm">
            {CODE_AGENT_TIER_LABEL[task.tier ?? CODE_AGENT_DEFAULT_TIER]} tier
          </span>
          <span className="text-xs opacity-50 font-mono">
            {CODE_AGENT_TIER_RESOURCES[task.tier ?? CODE_AGENT_DEFAULT_TIER]}
          </span>
        </div>

        {task.costUsd !== undefined && (
          <p className="text-xs opacity-50 pl-1">
            Cost: ${task.costUsd.toFixed(4)}
            {task.tokenUsage &&
              ` (${formatTokenCount(task.tokenUsage.inputTokens)} in / ${formatTokenCount(
                task.tokenUsage.outputTokens,
              )} out)`}
          </p>
        )}

        {(task.standaloneTaskId || task.resumedFromTaskId) && (
          <div className="flex items-center gap-2 pl-1 flex-wrap">
            {task.standaloneTaskId &&
              (linkedTask ? (
                <button
                  type="button"
                  className="badge badge-outline badge-sm gap-1 max-w-xs"
                  onClick={() => setIsLinkedTaskModalOpen(true)}
                  title="Open task preview"
                >
                  <LuBot size={11} className="shrink-0" />
                  <span className="truncate">
                    {linkedTask.key}: {linkedTask.title}
                  </span>
                </button>
              ) : (
                <span className="badge badge-outline badge-sm gap-1">
                  <LuBot size={11} /> Linked to a Standalone task
                </span>
              ))}
            {task.resumedFromTaskId && (
              <Link to={`/coding-agent/tasks/${task.resumedFromTaskId}`} className="badge badge-outline badge-sm gap-1">
                <LuGitPullRequest size={11} /> Resumed from a previous task
              </Link>
            )}
          </div>
        )}

        {(task.prUrl || task.prReviewStatus) && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <LuGitPullRequest size={16} className="opacity-60 shrink-0" />
                  {task.prUrl ? (
                    <a
                      href={task.prUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link link-primary text-sm"
                    >
                      {task.prOwner && task.prRepo ? `${task.prOwner}/${task.prRepo}` : 'View pull request'}
                      {task.prNumber != null && ` #${task.prNumber}`}
                    </a>
                  ) : (
                    <span className="text-sm opacity-60">Pull request</span>
                  )}
                  {task.prReviewStatus && (
                    <span className={`badge badge-sm ${PR_REVIEW_STATUS_BADGE[task.prReviewStatus]}`}>
                      {PR_REVIEW_STATUS_LABEL[task.prReviewStatus]}
                    </span>
                  )}
                  {!!task.prUnresolvedCommentCount && (
                    <span className="badge badge-outline badge-sm">
                      {task.prUnresolvedCommentCount} unresolved comment{task.prUnresolvedCommentCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {task.prReviewCheckedAt != null && (
                    <span className="text-xs opacity-40">Checked {formatRelativeTime(task.prReviewCheckedAt)}</span>
                  )}
                  <button
                    className="btn btn-ghost btn-xs gap-1"
                    onClick={handleCheckPrNow}
                    disabled={isCheckingPr}
                    title="Check the PR right now instead of waiting for the next scheduled poll"
                  >
                    {isCheckingPr ? <LuLoaderPinwheel className="animate-spin" size={12} /> : <LuRefreshCw size={12} />}
                    Check now
                  </button>
                </div>
              </div>

              {task.prReviewStatus && PR_REVIEW_RESUMABLE_STATUSES.includes(task.prReviewStatus) && (
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <input
                    type="text"
                    className="input input-bordered input-sm flex-1"
                    placeholder="Optional extra context for addressing the feedback…"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                  />
                  <button
                    className="btn btn-primary btn-sm gap-1.5 shrink-0"
                    onClick={handleResume}
                    disabled={isResuming}
                  >
                    {isResuming ? (
                      <LuLoaderPinwheel className="animate-spin" size={14} />
                    ) : (
                      <LuGitPullRequest size={14} />
                    )}
                    Resume — address feedback
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {task.status === 'completed' && task.resultSummary && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-1">
              <span className="text-xs font-medium opacity-50">Result</span>
              <MarkdownRenderer markdown={task.resultSummary} />
            </div>
          </div>
        )}
        {task.status === 'failed' && task.error && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-1">
              <span className="text-xs font-medium opacity-50">Result</span>
              <span className="text-sm">{task.error}</span>
            </div>
          </div>
        )}
        {task.status === 'cancelled' && (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-4 gap-1">
              <span className="text-xs font-medium opacity-50">Result</span>
              <span className="text-sm">{task.error ?? 'Stopped by user.'}</span>
            </div>
          </div>
        )}

        <div className="card bg-base-100 shadow-card">
          <button
            type="button"
            className="card-body p-4 gap-2 w-full text-left"
            onClick={() => setIsLogsOpen((v) => !v)}
          >
            <span className="text-xs font-medium opacity-50 flex items-center gap-1.5">
              {isLogsOpen ? <LuChevronDown size={13} /> : <LuChevronRight size={13} />}
              Execution log
            </span>
          </button>
          {isLogsOpen && (
            <div className="card-body pt-0 p-4">
              <div className="rounded-lg bg-base-200 p-3 max-h-[60vh] overflow-y-auto font-mono text-xs space-y-1">
                {logs.length === 0 ? (
                  <p className="opacity-40">
                    {task.status === 'queued'
                      ? task.queuePosition != null
                        ? `Waiting for a free sandbox — position ${task.queuePosition}…`
                        : 'Waiting to start…'
                      : 'No log entries yet.'}
                  </p>
                ) : (
                  logs.map((entry, i) => (
                    <div key={i} className={entry.isError ? 'text-error' : 'opacity-80'}>
                      <span className="opacity-40">[{new Date(entry.ts).toLocaleTimeString()}]</span> {entry.message}
                    </div>
                  ))
                )}
                {task.status === 'running' && (
                  <div className="flex items-center gap-1.5 opacity-50 pt-1">
                    <LuLoaderPinwheel className="animate-spin" size={11} />
                    working…
                  </div>
                )}
                <div ref={logsEndRef} />
              </div>
            </div>
          )}
        </div>
      </div>

      {isLinkedTaskModalOpen && linkedTask && (
        <LinkedTaskPreviewModal task={linkedTask} onClose={() => setIsLinkedTaskModalOpen(false)} />
      )}
    </div>
  );
}
